import {useEffect, useRef} from 'react';

import {useSelector} from '../../state/hooks';
import type * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';

export const useIsSectionPinned = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  return useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].pinned
  );
};

/**
 * This is used to show a border around a section when it is pinned.
 */
export const useShowPinnedBorder = (
  sectionRef: PanelBankSectionConfigTypes.Ref,
  sectionComponentRef: React.RefObject<HTMLDivElement | null>
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showPinnedBorderRef = useRef<{sectionRefId: string} | null>(null);
  const isPinned = useIsSectionPinned(sectionRef);

  const isInitialMount = useRef(true);
  const previousIsPinned = useRef(isPinned);

  useEffect(() => {
    // Skip flashing border on initial mount if already pinned.
    // This can be tested by refreshing a page with pinned sections.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousIsPinned.current = isPinned;
      return;
    }

    // Only trigger animation when transitioning from unpinned to pinned
    if (isPinned && !previousIsPinned.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      showPinnedBorderRef.current = {sectionRefId: sectionRef.id};
      sectionComponentRef.current?.scrollTo({
        top: 0,
      });

      timeoutRef.current = setTimeout(() => {
        showPinnedBorderRef.current = null;
        timeoutRef.current = null;
      }, 800);
    }

    previousIsPinned.current = isPinned;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isPinned, sectionComponentRef, sectionRef.id]);

  return {showPinnedBorderRef};
};
